const _excluded = ["onClose", "onSave", "testId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useMemo, useRef, useState } from 'react';
import produce from 'immer';
import { pathgen } from 'object-path-generator';
import { Typography, ConfirmationModal, Divider } from 'seagull';
import { useIoContext, useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useScrollBorder } from "../../hooks/useScrollBorder";
import { serviceModel } from "../../models";
import { useValidateService } from "../../models/service/hooks/useValidateService";
import { useGetSettings } from "../ServiceAdvancedSettings/hooks/useGetSettings/useGetSettings";
import { renderAdvancedSettingControl } from "../ServiceAdvancedSettings/ServiceAdvancedSettings";
import { AdvancedSettingCategory, ADVANCED_SETTING_CATEGORY_MAP } from "../ServiceAdvancedSettings/ServiceAdvancedSettings.types";
import styles from "./ServiceAdvancedSettingsModal.module.scss";
import { createLocalActionContext } from "./ServiceAdvancedSettingsModal.utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const {
  ServiceAdvancedSettingsModalTestIds
} = pathgen();
export const ServiceAdvancedSettingsModal = _ref => {
  var _rest$isFreshlyAdded;
  let {
      onClose,
      onSave,
      testId = 'service-advanced-settings-modal'
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [localService, setLocalService] = useState(() => produce(rest.originalService || rest.service, () => {}));
  const [showServiceValidationWarning, setShowServiceValidationWarning] = useState(false);
  const {
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const now = useMemo(() => getNow(), [getNow]);
  const featureFlags = useFeatureFlags();
  const isApprovedRelationship = rest.relationshipState.type === 'approved';
  const effectiveDate = rest.relationshipState.effectiveDate;
  const {
    isInvalid
  } = useValidateService();
  const contentRef = useRef(null);
  const isFreshlyAdded = (_rest$isFreshlyAdded = rest.isFreshlyAdded) !== null && _rest$isFreshlyAdded !== void 0 ? _rest$isFreshlyAdded : !rest.originalService;
  const updateLocalService = useCallback(updater => {
    setLocalService(prev => updater(prev));
  }, []);
  const localActionContext = useMemo(() => createLocalActionContext({
    localService,
    now,
    relationshipState: rest.relationshipState,
    effectiveDate,
    isApprovedRelationship,
    updateLocalService,
    featureFlags,
    logBI
  }), [localService, now, rest.relationshipState, effectiveDate, isApprovedRelationship, updateLocalService, featureFlags, logBI]);
  const localSettings = useGetSettings({
    isFreshlyAdded,
    isApprovedRelationship,
    service: localService,
    now,
    effectiveDate
  });
  const settingsByCategory = useMemo(() => {
    const grouped = {
      [AdvancedSettingCategory.Pricing]: [],
      [AdvancedSettingCategory.Billing]: [],
      [AdvancedSettingCategory.Integrations]: []
    };
    localSettings.forEach(setting => {
      const category = ADVANCED_SETTING_CATEGORY_MAP[setting.type];
      grouped[category].push(setting);
    });
    return grouped;
  }, [localSettings]);
  const categories = [AdvancedSettingCategory.Pricing, AdvancedSettingCategory.Billing, AdvancedSettingCategory.Integrations];
  const {
    footerClassName,
    headerClassName
  } = useScrollBorder({
    footerBaseClassName: styles.footer,
    contentRef: contentRef,
    enabled: true
  }, [localSettings, localService]);
  const handleSave = useCallback(() => {
    const hasSettingsErrors = isInvalid(localService, {
      relationshipEffectiveDate: effectiveDate,
      isTemplate: rest.relationshipState.type === 'template',
      excludeBillingValidation: true
    });
    if (hasSettingsErrors) {
      setShowServiceValidationWarning(true);
      return;
    }
    setShowServiceValidationWarning(false);
    onSave(localService);
  }, [localService, isInvalid, effectiveDate, rest.relationshipState.type, onSave]);
  const renderControl = setting => {
    const controlProps = _objectSpread(_objectSpread(_objectSpread({}, rest), {}, {
      service: serviceModel.applyChanges(localService),
      testId: testId + '-modal',
      showServiceValidationWarning
    }, setting), {}, {
      onPausedChange: localActionContext.onPausedChange,
      onBillOnAcceptanceChange: localActionContext.onBillOnAcceptanceChange,
      onProratedChange: localActionContext.onProratedChange,
      onOptionalChange: localActionContext.onOptionalChange,
      onLimitBillingTimesChange: localActionContext.onLimitBillingTimesChange,
      onAutoPriceIncreaseChange: localActionContext.onAutoPriceIncreaseChange,
      onQboClassChange: localActionContext.onQboClassChange,
      onKarbonChange: localActionContext.onKarbonChange,
      onFinancialCentsChange: localActionContext.onFinancialCentsChange,
      onLimitedTimeDiscountOfferChange: localActionContext.onLimitedTimeDiscountOfferChange,
      onDiscountPeriodExpirationChange: localActionContext.onDiscountPeriodExpirationChange,
      onPreapprovedAmountChange: localActionContext.onPreapprovedAmountChange,
      onStartBillingAtChange: localActionContext.onStartBillingAtChange,
      featureFlags
    });
    return /*#__PURE__*/_jsx("div", {
      "data-testid": ServiceAdvancedSettingsModalTestIds.Setting(ADVANCED_SETTING_CATEGORY_MAP[setting.type], setting.type),
      children: renderAdvancedSettingControl(setting, controlProps, localService)
    }, setting.type);
  };
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: "Advanced settings",
    onCloseButtonClick: onClose,
    "data-testid": ServiceAdvancedSettingsModalTestIds.Root(),
    primaryButtonLabel: "Save",
    secondaryButtonLabel: "Cancel",
    width: "700px",
    className: styles.modalClassName,
    footerClassName: footerClassName,
    contentClassName: styles.contentClassName,
    contentRef: contentRef,
    bodyClassName: styles.bodyClassName,
    headerClassName: headerClassName,
    onPrimaryButtonClick: handleSave,
    onSecondaryButtonClick: onClose,
    content: /*#__PURE__*/_jsx(_Fragment, {
      children: categories.map(category => {
        const settings = settingsByCategory[category];
        if (settings.length === 0) return null;
        return /*#__PURE__*/_jsxs("div", {
          className: styles.category,
          "data-testid": ServiceAdvancedSettingsModalTestIds.Category(category),
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "paragraph1",
            skin: "secondary",
            children: category
          }), settings.map(setting => renderControl(setting)), category !== categories[categories.length - 1] && /*#__PURE__*/_jsx(Divider, {
            margin: 20
          })]
        }, category);
      })
    })
  });
};